import {useState, useEffect, useCallback} from 'react';
import {Store} from '../models/store.model';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {storeFactory} from '../store/storeFactory';

interface UseStoreDetailsProps {
  storeId: string | null;
  isOpen: boolean;
  stores?: Store[];
}

export const useStoreDetails = ({storeId, isOpen, stores}: UseStoreDetailsProps) => {
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStoreDetails = useCallback(async () => {
    if (!storeId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try to find the store from the stores array first
      if (stores && stores.length > 0) {
        const foundStore = stores.find(s => s.id.toString() === storeId);
        if (foundStore) {
          setStore(foundStore);
          setLoading(false);
          return;
        }
      }

      // Fetch from API when not in list (e.g. when opened from map via "عرض تفاصيل المتجر")
      const {getStoreDetails} = storeFactory.getUseCases();
      const fullStore = await getStoreDetails.execute(storeId);
      setStore(fullStore);
    } catch (err) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(err);
      setError(appError.message || 'حدث خطأ في جلب تفاصيل المتجر');
      errorHandlingService.handleError(err); // For notifications/logging
    } finally {
      setLoading(false);
    }
  }, [storeId, stores]);

  // Load store details when modal opens
  useEffect(() => {
    if (isOpen && storeId) {
      loadStoreDetails();
    } else {
      setStore(null);
      setError(null);
    }
  }, [isOpen, storeId, loadStoreDetails]);

  return {
    store,
    loading,
    error
  };
};


